import { Injectable, OnDestroy } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { PermissionsService } from 'app/core/permissions/permissions.service';
import { cloneDeep } from 'lodash-es';
import { Navigation } from './navigation.types';
import { TranslocoService } from '@ngneat/transloco';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class NavigationService implements OnDestroy {
    private _navigation: BehaviorSubject<Navigation> =
        new BehaviorSubject<Navigation>(null);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private _navigationItems: FuseNavigationItem[] = [
        {
            id: 'dashboard',
            translationKey: 'navigation.dashboard',
            type: 'basic',
            icon: 'custom-dashboard',
            link: '/dashboard',
        },
        {
            id: 'players',
            translationKey: 'navigation.players.title',
            title: 'Players',
            type: 'collapsable',
            icon: 'custom-players',
            children: [
                {
                    id: 'list',
                    translationKey: 'navigation.players.title',
                    title: 'Players',
                    type: 'basic',
                    link: '/players',
                    exactMatch: true,
                },
                // {
                //     id: 'kpi-report',
                //     translationKey: 'navigation.players.kpi-report',
                //     title: "Players' KPI Report",
                //     type: 'basic',
                //     link: '/players/kpi-report',
                //     exactMatch: true,
                // },
                // {
                //     id: 'limit-request',
                //     translationKey: 'navigation.players.limit-request',
                //     title: 'Change Limit Requests',
                //     type: 'basic',
                //     link: '/players/limit-request',
                //     exactMatch: true,
                // },
                {
                    id: 'messages-list',
                    translationKey: 'players.messages-list.title',
                    title: 'Messages List',
                    type: 'basic',
                    link: '/players/messages-list',
                    exactMatch: true,
                },
                {
                    id: 'player-register-sessions',
                    translationKey: 'players.register-sessions.title',
                    title: 'Player Register Sessions',
                    type: 'basic',
                    link: '/players/player-register-sessions',
                    exactMatch: true,
                },
                // {
                //     id: 'locked',
                //     title: 'Locked Players',
                //     type: 'basic',
                //     link: '/players/locked',
                //     exactMatch: true,
                // },
            ],
        },
        {
            id: 'financials',
            translationKey: 'navigation.financials.title',
            title: 'Financials',
            type: 'collapsable',
            icon: 'custom-financials',
            children: [
                {
                    id: 'cashier',
                    translationKey: 'navigation.financials.cashier',
                    title: 'Cashier',
                    type: 'basic',
                    link: '/financials/cashier',
                    exactMatch: true,
                },
                {
                    id: 'transactions',
                    translationKey: 'navigation.financials.transactions',
                    title: 'Transactions',
                    type: 'basic',
                    link: '/financials/transactions',
                    exactMatch: true,
                },
                {
                    id: 'exchange-log',
                    translationKey: 'navigation.financials.exchange-log',
                    type: 'basic',
                    link: '/financials/exchange-log',
                    exactMatch: true,
                },
                {
                    id: 'verification-queue',
                    translationKey: 'navigation.players.verification-queue',
                    title: 'Player Verification Queue',
                    type: 'basic',
                    link: '/financials/verification-queue',
                    exactMatch: true,
                },
                {
                    id: 'withdrawals',
                    translationKey: 'navigation.players.withdrawals',
                    title: 'Player Withdrawals',
                    type: 'basic',
                    link: '/financials/withdrawals',
                    exactMatch: true,
                },
            ],
        },
        // {
        //     id: 'casino-content',
        //     translationKey: 'navigation.casinoContent',
        //     type: 'basic',
        //     icon: 'custom-casino',
        //     link: '/casino-content',
        // },
        // {
        //     id: 'sport-content',
        //     translationKey: 'navigation.sportContent',
        //     type: 'basic',
        //     icon: 'custom-sports',
        //     link: '/sport-content',
        // },
        {
            id: 'promotions',
            translationKey: 'navigation.promotions',
            type: 'basic',
            icon: 'custom-promotions',
            link: '/promotional-tools/bonuses',
        },
        {
            id: 'campaigns',
            translationKey: 'navigation.campaigns.title',
            title: 'Campaigns',
            type: 'collapsable',
            icon: 'custom-promotions',
            children: [
                {
                    id: 'free-chips-campaign',
                    translationKey: 'navigation.campaigns.free-chips',
                    title: 'Free Chips Campaign',
                    type: 'basic',
                    link: '/campaigns/free-chips',
                    exactMatch: true,
                },
                {
                    id: 'free-spin-campaign',
                    translationKey: 'navigation.campaigns.free-spin',
                    title: 'Free Spin Campaign',
                    type: 'basic',
                    link: '/campaigns/free-spin',
                    exactMatch: true,
                },
            ],
        },
        // {
        //     id: 'brand-management',
        //     translationKey: 'navigation.brandManagement',
        //     type: 'basic',
        //     icon: 'custom-brand',
        //     link: '/brand-management',
        // },
        {
            id: 'reports',
            translationKey: 'navigation.reports.title',
            title: 'Reports',
            type: 'collapsable',
            icon: 'custom-report',
            children: [
                {
                    id: 'sport-book',
                    translationKey: 'navigation.reports.sport-book.title',
                    title: 'SportBook',
                    type: 'collapsable',
                    children: [
                        {
                            id: 'bets',
                            translationKey:
                                'navigation.reports.sport-book.bets',
                            title: 'Bets Report',
                            type: 'basic',
                            link: '/reports/sport-book/bets',
                            exactMatch: true,
                        },
                        {
                            id: 'sports',
                            translationKey: 'reports.sport-book.sports.title',
                            title: 'Sports Report',
                            type: 'basic',
                            link: '/reports/sport-book/sports',
                            exactMatch: true,
                        },
                        {
                            id: 'competitions',
                            translationKey:
                                'navigation.reports.sport-book.competitions',
                            title: 'Competitions Report',
                            type: 'basic',
                            link: '/reports/sport-book/competitions',
                            exactMatch: true,
                        },
                        {
                            id: 'market-types',
                            translationKey:
                                'navigation.reports.sport-book.market-types',
                            title: 'Market Types Report',
                            type: 'basic',
                            link: '/reports/sport-book/market-types',
                            exactMatch: true,
                        },
                    ],
                },
                {
                    id: 'casino',
                    translationKey: 'navigation.reports.casino.title',
                    title: 'Casino',
                    type: 'collapsable',
                    children: [
                        {
                            id: 'game',
                            translationKey: 'navigation.reports.casino.game',
                            title: 'Report By Game',
                            type: 'basic',
                            link: '/reports/casino/game',
                            exactMatch: true,
                        },
                        {
                            id: 'player',
                            translationKey: 'navigation.reports.casino.player',
                            title: 'Report By Player',
                            type: 'basic',
                            link: '/reports/casino/player',
                            exactMatch: true,
                        },
                        {
                            id: 'provider',
                            translationKey:
                                'navigation.reports.casino.provider',
                            title: 'Report By Provider',
                            type: 'basic',
                            link: '/reports/casino/provider',
                            exactMatch: true,
                        },
                        {
                            id: 'player-game',
                            translationKey:
                                'navigation.reports.casino.player-game',
                            title: 'Report By Player Game',
                            type: 'basic',
                            link: '/reports/casino/player-game',
                            exactMatch: true,
                        },
                    ],
                },
                {
                    id: 'financial',
                    translationKey: 'navigation.reports.financial.title',
                    title: 'Financial',
                    type: 'collapsable',
                    children: [
                        {
                            id: 'payments-report',
                            translationKey:
                                'navigation.reports.financial.payments',
                            title: 'Payments Report',
                            type: 'basic',
                            link: '/reports/financial/payments-report',
                            exactMatch: true,
                        },
                        {
                            id: 'monthly-kpi-report',
                            translationKey: 'navigation.reports.financial.monthly-kpi-report',
                            title: 'Monthly KPI Report',
                            type: 'basic',
                            link: '/reports/financial/monthly-kpi-report',
                            exactMatch: true,
                        }
                    ],
                },
                // {
                //     id: 'financial',
                //     translationKey: 'navigation.reports.financial.title',
                //     title: 'Financial',
                //     type: 'collapsable',
                //     children: [
                //         {
                //             id: 'payments',
                //             translationKey:
                //                 'navigation.reports.financial.payments',
                //             title: 'Payments Report',
                //             type: 'basic',
                //             link: '/reports/financial/payments',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'region-payments',
                //             translationKey:
                //                 'navigation.reports.financial.region-payments',
                //             title: 'Payments By Region',
                //             type: 'basic',
                //             link: '/reports/financial/region-payments',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'account-balance',
                //             translationKey:
                //                 'navigation.reports.financial.account-balance',
                //             title: 'Account Balance Report',
                //             type: 'basic',
                //             link: '/reports/financial/account-balance',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'sales',
                //             translationKey:
                //                 'navigation.reports.financial.sales',
                //             title: 'Sales Report',
                //             type: 'basic',
                //             link: '/reports/financial/sales',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'daily-sales',
                //             translationKey:
                //                 'navigation.reports.financial.daily-sales',
                //             title: 'Daily Sales Report',
                //             type: 'basic',
                //             link: '/reports/financial/daily-sales',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'balance',
                //             translationKey:
                //                 'navigation.reports.financial.balance',
                //             title: 'Balance Report',
                //             type: 'basic',
                //             link: '/reports/financial/balance',
                //             exactMatch: true,
                //         },
                //     ],
                // },
                // {
                //     id: 'betshops',
                //     translationKey: 'navigation.reports.betshops.title',
                //     title: 'Betshops',
                //     type: 'collapsable',
                //     children: [
                //         {
                //             id: 'cash-desk',
                //             translationKey:
                //                 'navigation.reports.betshops.cash-desk',
                //             title: 'Cash Desk Report',
                //             type: 'basic',
                //             link: '/reports/betshops/cash-desk',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'terminal',
                //             translationKey:
                //                 'navigation.reports.betshops.terminal',
                //             title: 'Terminal Report',
                //             type: 'basic',
                //             link: '/reports/betshops/terminal',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'betshop',
                //             translationKey:
                //                 'navigation.reports.betshops.betshop',
                //             title: 'Betshop Report',
                //             type: 'basic',
                //             link: '/reports/betshops/betshop',
                //             exactMatch: true,
                //         },
                //         {
                //             id: 'daily-betshop',
                //             translationKey:
                //                 'navigation.reports.betshops.daily-betshop',
                //             title: 'Daily Betshop Report',
                //             type: 'basic',
                //             link: '/reports/betshops/daily-betshop',
                //             exactMatch: true,
                //         },
                //     ],
                // },
                // {
                //     id: 'players-report',
                //     translationKey: 'navigation.reports.players-report',
                //     title: 'Players Report',
                //     type: 'basic',
                //     link: '/reports/players',
                //     exactMatch: true,
                // },
            ],
        },
        // {
        //     id: 'affiliates',
        //     translationKey: 'navigation.affiliates',
        //     type: 'basic',
        //     icon: 'custom-affiliates',
        //     link: '/affiliates',
        // },
        {
            id: 'users',
            translationKey: 'navigation.users.title',
            title: 'Users',
            type: 'collapsable',
            icon: 'custom-users',
            hidden: (_item) => {
                const hide =
                    !this.permissionsService.hasPermission('READ_ADMIN') &&
                    !this.permissionsService.hasPermission('READ_MANAGER') &&
                    !this.permissionsService.hasPermission('READ_OPERATOR');
                return hide;
            },
            children: [
                {
                    id: 'admin',
                    translationKey: 'navigation.users.admin',
                    title: 'Admins',
                    type: 'basic',
                    link: '/users/admins',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_ADMIN'
                        );
                    },
                },
                {
                    id: 'admin-group',
                    translationKey: 'navigation.users.admin-group',
                    title: 'Admin Groups',
                    type: 'basic',
                    link: '/users/admin-groups',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_ADMIN'
                        );
                    },
                },
                {
                    id: 'automatic-admin-group',
                    translationKey: 'navigation.users.automatic-admin-group',
                    title: 'Automatic Admin Groups',
                    type: 'basic',
                    link: '/users/admins/automatic-groups',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_ADMIN'
                        );
                    },
                },
                {
                    id: 'managers',
                    translationKey: 'navigation.users.managers',
                    title: 'Managers',
                    type: 'basic',
                    link: '/users/managers',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_MANAGER'
                        );
                    },
                },
                {
                    id: 'manager-group',
                    translationKey: 'navigation.users.manager-groups',
                    title: 'Manager Groups',
                    type: 'basic',
                    link: '/users/manager-groups',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_MANAGER'
                        );
                    },
                },
                {
                    id: 'automatic-manager-group',
                    translationKey: 'navigation.users.automatic-manager-group',
                    title: 'Automatic Manager Groups',
                    type: 'basic',
                    link: '/users/managers/automatic-groups',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_MANAGER'
                        );
                    },
                },
                {
                    id: 'operators',
                    translationKey: 'navigation.users.operators',
                    title: 'Operators',
                    type: 'basic',
                    link: '/users/operators',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_OPERATOR'
                        );
                    },
                },
                {
                    id: 'operator-group',
                    translationKey: 'navigation.users.operator-groups',
                    title: 'Operator Groups',
                    type: 'basic',
                    link: '/users/operator-groups',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_OPERATOR'
                        );
                    },
                },
                {
                    id: 'automatic-operator-group',
                    translationKey: 'navigation.users.automatic-operator-group',
                    title: 'Automatic Operator Groups',
                    type: 'basic',
                    link: '/users/operators/automatic-groups',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_OPERATOR'
                        );
                    },
                },
            ],
        },
        {
            id: 'betshop-management',
            title: 'Betshop Management',
            type: 'collapsable',
            hidden: () => {
                return true;
            },
            icon: 'heroicons_outline:building-storefront',
            children: [
                {
                    id: 'betshops',
                    title: 'Betshops',
                    type: 'basic',
                    link: '/betshop-management',
                    exactMatch: true,
                    hidden: () => {
                        return true;
                    },
                },
                {
                    id: 'cashdesks',
                    title: 'Cashdesks',
                    type: 'basic',
                    link: '/betshop-management/cashdesks',
                    exactMatch: true,
                    hidden: () => {
                        return true;
                    },
                },
            ],
        },
        {
            id: 'template-editor',
            translationKey: 'navigation.template-editor',
            title: 'Template Editor',
            type: 'collapsable',
            icon: 'custom-template',
            children: [
                {
                    id: 'sports',
                    translationKey: 'settings.template-editor.sports.title',
                    title: 'Sports',
                    type: 'basic',
                    link: '/settings/template/editor',
                },
                {
                    id: 'markets',
                    translationKey: 'settings.template-editor.markets.title',
                    title: 'Markets',
                    type: 'basic',
                    link: '/settings/template/markets',
                },
            ],
        },
        {
            id: 'email',
            translationKey: 'navigation.email.title',
            title: 'Email',
            type: 'collapsable',
            icon: 'heroicons_outline:envelope',
            children: [
                {
                    id: 'templates',
                    translationKey: 'navigation.email.templates',
                    title: 'Email Templates',
                    type: 'basic',
                    link: '/email/templates',
                    exactMatch: true,
                },
                {
                    id: 'logs',
                    translationKey: 'navigation.email.logs',
                    title: 'Email Logs',
                    type: 'basic',
                    link: '/email/logs',
                    exactMatch: true,
                },
            ],
        },
        {
            id: 'casino',
            translationKey: 'navigation.casino.title',
            title: 'Casino Aggregator',
            type: 'collapsable',
            icon: 'custom-casino',
            hidden: (_item) => {
                const hide =
                    !this.permissionsService.hasPermission('READ_CASINO_GAME') &&
                    !this.permissionsService.hasPermission('READ_CASINO_CATEGORY') &&
                    !this.permissionsService.hasPermission('READ_CASINO_PROVIDER') &&
                    !this.permissionsService.hasPermission('READ_CASINO_AUDIT_LOG');
                return hide;
            },
            children: [
                {
                    id: 'casino-games',
                    translationKey: 'navigation.casino.games',
                    title: 'Games',
                    type: 'basic',
                    link: '/casino/games',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_CASINO_GAME'
                        );
                    },
                },
                {
                    id: 'casino-categories',
                    translationKey: 'navigation.casino.categories',
                    title: 'Categories',
                    type: 'basic',
                    link: '/casino/categories',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_CASINO_CATEGORY'
                        );
                    },
                },
                {
                    id: 'casino-providers',
                    translationKey: 'navigation.casino.providers',
                    title: 'Providers',
                    type: 'basic',
                    link: '/casino/providers',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_CASINO_PROVIDER'
                        );
                    },
                },
                {
                    id: 'casino-logs',
                    translationKey: 'navigation.casino.auditLogs',
                    title: 'Audit Logs',
                    type: 'basic',
                    link: '/casino/logs',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_CASINO_AUDIT_LOG'
                        );
                    },
                },
            ],
        },
        {
            id: 'settings',
            translationKey: 'navigation.settings.title',
            title: 'Settings',
            type: 'collapsable',
            icon: 'custom-settings',
            hidden: (_item) => {
                const hide =
                    !this.permissionsService.hasPermission('READ_STORY') &&
                    !this.permissionsService.hasPermission('READ_BANNER') &&
                    !this.permissionsService.hasPermission(
                        'READ_NOTIFICATION_CONFIG'
                    );
                return hide;
            },
            children: [
                {
                    id: 'stories',
                    translationKey: 'navigation.settings.stories',
                    title: 'Stories',
                    type: 'basic',
                    link: '/settings/stories',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_STORY'
                        );
                    },
                },
                {
                    id: 'offers',
                    translationKey: 'navigation.settings.offers',
                    title: 'Offers',
                    type: 'basic',
                    link: '/settings/offers',
                    exactMatch: true,
                },
                {
                    id: 'banners',
                    translationKey: 'navigation.settings.banners',
                    type: 'basic',
                    link: '/settings/banners',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_BANNER'
                        );
                    },
                },
                {
                    id: 'notifications',
                    translationKey: 'navigation.settings.notifications',
                    title: 'Notifications',
                    type: 'basic',
                    link: '/settings/notifications',
                    exactMatch: true,
                    hidden: (_item) => {
                        return !this.permissionsService.hasPermission(
                            'READ_NOTIFICATION_CONFIG'
                        );
                    },
                },
                {
                    id: 'highlights',
                    translationKey: 'navigation.settings.highlights',
                    title: 'Highlights',
                    type: 'basic',
                    link: '/settings/highlights',
                    exactMatch: true,
                },
                {
                    id: 'payment-providers',
                    translationKey: 'navigation.settings.payment-providers',
                    type: 'basic',
                    link: '/settings/payment-providers',
                    exactMatch: true,
                },
                {
                    id: 'casino-management',
                    translationKey: 'Casino Management',
                    title: 'Casino Management',
                    type: 'basic',
                    link: '/settings/casino-management',
                    exactMatch: true,
                },
                {
                    id: 'configurations',
                    translationKey: 'settings.configurations.title',
                    title: 'Configurations',
                    type: 'basic',
                    link: '/settings/configurations',
                    exactMatch: true,
                },
            ],
        },
    ];

    constructor(
        private permissionsService: PermissionsService,
        private translocoService: TranslocoService,
        matIconRegistry: MatIconRegistry,
        domSanitizer: DomSanitizer
    ) {
        this.setupTranslations();
        matIconRegistry.addSvgIcon(
            'custom-dashboard',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/dashboard.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-users',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/users.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-players',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/players.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-report',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/report.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-financials',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/financials.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-template',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/template.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-settings',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/settings.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-casino',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/casino.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-sports',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/sports.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-promotions',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/gift.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-brand',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/brand.svg'
            )
        );
        matIconRegistry.addSvgIcon(
            'custom-affiliates',
            domSanitizer.bypassSecurityTrustResourceUrl(
                'images/navigation/affiliate.svg'
            )
        );
    }

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    private setupTranslations(): void {
        combineLatest([
            this.translocoService.langChanges$,
            this.translocoService.events$.pipe(
                filter((event) => event.type === 'translationLoadSuccess')
            ),
        ])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.updateNavigationTranslations();
            });
    }

    private updateNavigationTranslations(): void {
        const translatedNavigation = this.translateNavigationItems(
            this._navigationItems
        );

        const navigationData: Navigation = {
            compact: cloneDeep(translatedNavigation),
            default: cloneDeep(translatedNavigation),
            futuristic: cloneDeep(translatedNavigation),
            horizontal: cloneDeep(translatedNavigation),
        };

        this._navigation.next(navigationData);
    }

    private translateNavigationItems(
        items: FuseNavigationItem[]
    ): FuseNavigationItem[] {
        return items.map((item) => {
            const translatedItem = { ...item };
            if (item.translationKey) {
                translatedItem.title = this.translocoService.translate(
                    item.translationKey
                );
            }
            if (item.children) {
                translatedItem.children = this.translateNavigationItems(
                    item.children
                );
            }
            return translatedItem;
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
